import React from "react";
import {
  Grid,
  Paper,
  Typography,
  List,
  ListItem,
  ListItemButton,
  Fab,
  IconButton,
  Checkbox,
  FormGroup,
  FormControlLabel,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import NewOrgDialog from "./components/NewOrgDialog";
import NewAdminUserDialog from "./components/NewAdminUserDialog";
import EditAdminUserDialog from "./components/EditAdminUserDialog";
import Deployments from "./components/Deployments";
import Licensing from "./components/Licensing";
import { EventContext } from "../../contexts/EventContext";
import request from "../../utilities/requests";

const Admin = () => {
  const navigate = useNavigate();
  const { userData } = React.useContext(EventContext);
  const [orgs, setOrgs] = React.useState([]);
  const [users, setUsers] = React.useState([]);
  const [newOrgOpen, setNewOrgOpen] = React.useState(false);
  const [selectedOrg, setSelectedOrg] = React.useState(null);
  const [editOrg, setEditOrg] = React.useState(null);
  const [newUserOpen, setNewUserOpen] = React.useState(false);
  const [selectedUser, setSelectedUser] = React.useState(null);
  const [editUserOpen, setEditUserOpen] = React.useState(false);
  const [showLicensing, setShowLicensing] = React.useState(false);

  React.useEffect(() => {
    if (orgs.length === 0) {
      getOrgs();
    }
  });

  React.useEffect(() => {
    if (selectedOrg) {
      getUsers();
    }
  }, [selectedOrg]);

  React.useEffect(() => {
    if (
      userData &&
      (userData?.role === "superadmin" ||
        userData?.permissions?.canViewContract === true ||
        userData?.permissions?.canViewInvoicing === true)
    ) {
      setShowLicensing(true);
    }
  }, [userData]);

  const getOrgs = async () => {
    const result = await request("orgs");
    if (result.success) {
      if (result.orgs) {
        setOrgs([...result.orgs]);
      } else if (result.org) {
        setOrgs([result.org]);
        setSelectedOrg(result?.org);
      }
    }
  };

  const getUsers = async () => {
    const result = await request(`users/adminusers?orgId=${selectedOrg._id}`);
    if (result.success) {
      setUsers(result.users);
    }
  };

  const updateServices = async (name) => {
    let value;
    if (!selectedOrg[name]) {
      value = true;
    } else {
      value = false;
    }

    const body = {
      orgId: selectedOrg._id,
      [name]: value,
    };
    const updateResult = await request("orgs/update", "post", body);
    if (updateResult?.success) {
      setSelectedOrg({
        ...selectedOrg,
        [name]: value,
      });
    }
  };

  return (
    <Grid container spacing={3} style={{ padding: 20 }}>
      <Grid item xs={12} md={4}>
        <Paper
          elevation={3}
          style={{
            width: "100%",
            height: 300,
            padding: 10,
            position: "relative",
          }}
        >
          <Grid container direction="column">
            <Grid item>
              <Typography variant="h5">Orgs</Typography>
            </Grid>
            <Grid item>
              <List
                style={{
                  maxHeight: 200,
                  overflowY: "scroll",
                  marginBottom: 10,
                }}
              >
                {orgs.map((org) => {
                  return (
                    <ListItem
                      key={org._id}
                      secondaryAction={
                        userData?.permissions?.canEditOrg ||
                        userData?.role === "superadmin" ? (
                          <IconButton
                            edge="end"
                            onClick={() => {
                              setEditOrg(org);
                              setNewOrgOpen(true);
                            }}
                          >
                            <EditIcon />
                          </IconButton>
                        ) : (
                          <></>
                        )
                      }
                    >
                      <ListItemButton onClick={() => setSelectedOrg(org)}>
                        {org.name}
                      </ListItemButton>
                    </ListItem>
                  );
                })}
              </List>
            </Grid>
            <Grid
              item
              style={{
                display: "flex",
                direction: "row",
                justifyContent: "flex-end",
              }}
            ></Grid>
            {userData?.role === "superadmin" ? (
              <Fab
                size="small"
                color="primary"
                style={{ position: "absolute", bottom: 0, right: 0 }}
                onClick={() => {
                  setEditOrg(null);
                  setNewOrgOpen(true);
                }}
              >
                <AddIcon />
              </Fab>
            ) : (
              <></>
            )}
          </Grid>
        </Paper>
      </Grid>
      <Grid item xs={12} md={4}>
        <Paper
          elevation={3}
          style={{
            width: "100%",
            height: 300,
            padding: 10,
            position: "relative",
          }}
        >
          <Grid container direction="column">
            <Grid item>
              <Typography variant="h5">{selectedOrg?.name} Users</Typography>
            </Grid>
            <Grid item>
              {selectedOrg ? (
                <List
                  style={{
                    maxHeight: 200,
                    overflowY: "scroll",
                    marginBottom: 10,
                  }}
                >
                  {users.map((user) => {
                    return (
                      <ListItem key={user._id}>
                        <ListItemButton
                          onClick={() => {
                            setSelectedUser(user);
                            setEditUserOpen(true);
                          }}
                        >
                          {user?.profile?.firstName} {user?.profile?.lastName}
                        </ListItemButton>
                      </ListItem>
                    );
                  })}
                </List>
              ) : (
                <Typography>Select an Org</Typography>
              )}
            </Grid>
          </Grid>
          {selectedOrg ? (
            <Fab
              size="small"
              color="primary"
              style={{ position: "absolute", bottom: 0, right: 0 }}
              onClick={() => setNewUserOpen(true)}
            >
              <AddIcon />
            </Fab>
          ) : (
            <></>
          )}
        </Paper>
      </Grid>
      {userData?.role === "superadmin" ? (
        <Grid item xs={12} md={4}>
          <Paper
            elevation={3}
            style={{ width: "100%", height: 300, padding: 10 }}
          >
            <Grid container direction="column">
              <Typography variant="h5">Services</Typography>
            </Grid>
            <Grid item style={{ paddingLeft: 20 }}>
              {selectedOrg ? (
                <List
                  style={{
                    maxHeight: 200,
                    overflowY: "scroll",
                    marginBottom: 10,
                  }}
                >
                  <FormGroup>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={selectedOrg?.pairingActive ? true : false}
                          onChange={() => updateServices("pairingActive")}
                          disabled={
                            userData?.role === "superadmin" ? false : true
                          }
                        />
                      }
                      label="Pairing"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={
                            selectedOrg?.sportsAndWellnessActive ? true : false
                          }
                          onChange={() =>
                            updateServices("sportsAndWellnessActive")
                          }
                          disabled={
                            userData?.role === "superadmin" ? false : true
                          }
                        />
                      }
                      label="Sports & Wellness"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={
                            selectedOrg?.artsAndEntertainmentActive
                              ? true
                              : false
                          }
                          onChange={() =>
                            updateServices("artsAndEntertainmentActive")
                          }
                          disabled={
                            userData?.role === "superadmin" ? false : true
                          }
                        />
                      }
                      label="Arts & Entertainment"
                    />
                  </FormGroup>
                </List>
              ) : (
                <Typography>Select an Org</Typography>
              )}
            </Grid>
          </Paper>
        </Grid>
      ) : (
        <></>
      )}

      <Grid item xs={12} md={4}>
        <Deployments
          deployments={selectedOrg ? selectedOrg?.deployments : []}
          refresh={getOrgs}
        />
      </Grid>
      {showLicensing ? (
        <Grid item xs={12} md={4}>
          <Licensing org={selectedOrg} />
        </Grid>
      ) : null}

      <NewOrgDialog
        open={newOrgOpen}
        close={() => {
          setNewOrgOpen(false);
          getOrgs();
        }}
        org={editOrg}
      />
      <NewAdminUserDialog
        open={newUserOpen}
        close={() => setNewUserOpen(false)}
        orgId={selectedOrg ? selectedOrg._id : null}
        refresh={() => getUsers()}
      />
      <EditAdminUserDialog
        open={editUserOpen}
        close={() => {
          setEditUserOpen(false);
          getUsers();
        }}
        user={selectedUser}
        // superadmin={userData?.role === 'superadmin' ? true : false}
      />
    </Grid>
  );
};

export default Admin;
